import type { Vec3 } from '../math/vec3'

/**
 * `anchor.json` — the per-product fitting file written by the fitting studio and read by every SDK.
 * Lives next to `model.glb`. Version 1.
 */
export const ANCHOR_SPEC_VERSION = 1 as const

export type Category =
  | 'glasses'
  | 'earring'
  | 'nosepin'
  | 'bindi'
  | 'tikka'
  | 'necklace'
  | 'ring'
  | 'bracelet'
  | 'watch'
  | 'hat'
  | 'shoe'

export const CATEGORIES: readonly Category[] = [
  'glasses', 'earring', 'nosepin', 'bindi', 'tikka', 'necklace', 'ring', 'bracelet', 'watch', 'hat', 'shoe',
]

/** Named anchor origins. Each maps to a canonical point + a landmark used to refine it (see anchors.ts). */
export type FaceAnchorOrigin =
  | 'head_center'
  | 'nose_bridge'
  | 'nose_tip'
  | 'nostril_left'
  | 'nostril_right'
  | 'forehead'
  | 'glabella'
  | 'chin'
  | 'head_top'
  | 'ear_left'
  | 'ear_right'
  | 'ear_lobe_left'
  | 'ear_lobe_right'
  | 'neck'
export type HandAnchorOrigin =
  | 'wrist'
  | 'finger_thumb'
  | 'finger_index'
  | 'finger_middle'
  | 'finger_ring'
  | 'finger_pinky'
export type FootAnchorOrigin = 'foot_left' | 'foot_right'
export type AnchorOrigin = FaceAnchorOrigin | HandAnchorOrigin | FootAnchorOrigin

export const FACE_ANCHOR_ORIGINS: readonly FaceAnchorOrigin[] = [
  'head_center', 'nose_bridge', 'nose_tip', 'nostril_left', 'nostril_right', 'forehead', 'glabella', 'chin',
  'head_top', 'ear_left', 'ear_right', 'ear_lobe_left', 'ear_lobe_right', 'neck',
]
export const HAND_ANCHOR_ORIGINS: readonly HandAnchorOrigin[] = [
  'wrist', 'finger_thumb', 'finger_index', 'finger_middle', 'finger_ring', 'finger_pinky',
]
export const FOOT_ANCHOR_ORIGINS: readonly FootAnchorOrigin[] = ['foot_left', 'foot_right']

export type OccluderType = 'none' | 'face' | 'head' | 'face+head' | 'hand' | 'wrist' | 'foot'

export interface OccluderSpec {
  type: OccluderType
  /** Extra depth (cm) to push the occluder away from the camera (positive = deeper). */
  depthOffset: number
  /** Uniform scale multiplier for procedural occluders (head ellipsoid, wrist cylinder…). */
  scale: number
  /** Whether occluder should be visible for debugging. SDKs ignore; studio uses it. */
  debug?: boolean
}

export interface MaterialOverride {
  /** Hex colour like "#RRGGBB". */
  color?: string
  metalness?: number
  roughness?: number
  opacity?: number
  /** Optional replacement texture URL. */
  map?: string
}

export interface Variant {
  id: string
  name: string
  /** Optional per-variant GLB (otherwise the product's model.glb is used with material overrides). */
  glbUrl?: string
  /** Node/material-name → override. */
  materials?: Record<string, MaterialOverride>
  thumbnailUrl?: string
}

export interface GlassesHints {
  /** Names of the left/right temple nodes in the GLB (for ear-length auto-fit). */
  templeNodes?: [string, string]
  /** Names of lens meshes to apply the lens material to. */
  lensNodes?: string[]
  /** Hex tint for lenses (e.g. "#222222" for sunglasses). */
  lensTint?: string
  /** 0 = clear, 1 = opaque. */
  lensOpacity?: number
  /** Auto-scale temples so their tips reach the ear landmarks. */
  autoFitTemples?: boolean
}

export interface EarringHints {
  /** True = hangs under gravity (pendulum sim); false = rigid stud following the ear. */
  dangle?: boolean
  /** Effective pendulum length (cm). Defaults to the model's height below the pivot. */
  lengthCm?: number
  /** Pendulum damping override (1/s). */
  damping?: number
  /** Render on both ears (default true). */
  bothEars?: boolean
}

export interface RingHints {
  finger?: 'thumb' | 'index' | 'middle' | 'ring' | 'pinky'
  /** Which hand to render on; 'any' = first tracked hand. */
  hand?: 'left' | 'right' | 'any'
}

export interface AnchorSpec {
  version: typeof ANCHOR_SPEC_VERSION
  category: Category
  origin: AnchorOrigin
  /** Offset from the origin point, in the anchor's local frame (cm). */
  offset: Vec3
  /** Extra rotation, Euler XYZ degrees, applied in the anchor's local frame. */
  rotation: Vec3
  /** Fine-tune scale multiplier (1 = model authored to real size). */
  scale: number
  /** Model-units → cm. GLB is authored in metres → 100. */
  unitScale: number
  occluder: OccluderSpec
  variants: Variant[]
  /** Optional 2-D landmark refinement: snap the origin's screen position to this landmark index. */
  refineLandmark?: number | null
  glasses?: GlassesHints
  earring?: EarringHints
  ring?: RingHints
  meta?: { name?: string; brand?: string; sku?: string; [k: string]: unknown }
}

export const DEFAULT_OCCLUDER_BY_CATEGORY: Record<Category, OccluderType> = {
  glasses: 'face+head',
  // face mesh included: the jaw/cheek silhouette is what hides the far-side earring at small yaw
  earring: 'face+head',
  nosepin: 'face',
  bindi: 'face',
  tikka: 'face+head',
  necklace: 'face+head',
  ring: 'hand',
  bracelet: 'wrist',
  watch: 'wrist',
  hat: 'head',
  shoe: 'foot',
}

export const DEFAULT_ORIGIN_BY_CATEGORY: Record<Category, AnchorOrigin> = {
  glasses: 'nose_bridge',
  earring: 'ear_lobe_left',
  nosepin: 'nostril_left',
  bindi: 'glabella',
  tikka: 'forehead',
  necklace: 'neck',
  ring: 'finger_ring',
  bracelet: 'wrist',
  watch: 'wrist',
  hat: 'head_top',
  shoe: 'foot_left',
}

export function defaultAnchorSpec(category: Category): AnchorSpec {
  const spec: AnchorSpec = {
    version: ANCHOR_SPEC_VERSION,
    category,
    origin: DEFAULT_ORIGIN_BY_CATEGORY[category],
    offset: [0, 0, 0],
    rotation: [0, 0, 0],
    scale: 1,
    unitScale: 100,
    occluder: { type: DEFAULT_OCCLUDER_BY_CATEGORY[category], depthOffset: 0, scale: 1 },
    variants: [],
    refineLandmark: null,
  }
  if (category === 'glasses') spec.glasses = { autoFitTemples: true, lensOpacity: 0.15 }
  if (category === 'earring') spec.earring = { dangle: true, bothEars: true }
  if (category === 'ring') spec.ring = { finger: 'ring', hand: 'any' }
  return spec
}
